/**
 * THE VISIBLE PAGE AS A SNAPSHOT — what a host sends with a message: the portal while the person allowed whole-page
 * reading, wetalk.io the public page's own text (owner request 2026-10-18; ADR 0072; design §31.25 PAGE-READING row). Each host imports
 * it (`@wetalk/help-widget/snapshot`); the launcher and the panel never do.
 *
 * Reads the screen the person sees (the shell's main column, never the sidebar or the help panel) into the protocol's
 * `HelpPageSnapshot`: headings, text, status and error messages, tables (≤ 8 columns, ≤ 12 rows, the rest counted),
 * and every control with a ref, its label, type, value, options and whether it is enabled — a fill target's ref names
 * its form and field (`<form_id>.<code>`), any other control's is `c<n>`. Sizes are capped and every cut is marked;
 * the whole snapshot stays inside the protocol's byte limit.
 *
 * NEVER READ (redaction, by construction): a password, file or hidden input; a control whose autocomplete is a one-time
 * code, a password or a card field; a control whose name, id or label says password, secret, token, key, IBAN, card,
 * CVC, one-time code, 2FA or recovery code; anything inside `[data-help-never]` or a closed `<dialog>`; anything
 * hidden; scripts, styles and the help widget's host element (its root is closed anyway). Every text passes
 * `scrubSecretShape`, so a key or a card number shown on the page leaves as `[removed]`.
 *
 * Reads the DOM only: no request, no focus, no scroll, no mutation. Never throws.
 */
// Lean subpaths only: a host's island must not pull the domain barrel in (wetalk.io's third-party-origin check).
import { HELP_PAGE_SNAPSHOT_BYTE_LIMIT, HELP_SNAPSHOT_CELL_CHAR_LIMIT, HELP_SNAPSHOT_ITEM_LIMIT, HELP_SNAPSHOT_OPTION_LIMIT, HELP_SNAPSHOT_TABLE_COLUMN_LIMIT, HELP_SNAPSHOT_TABLE_ROW_LIMIT, HELP_SNAPSHOT_TEXT_CHAR_LIMIT, cutSnapshotText, } from "@wetalk/domain/help/page-snapshot";
import { utf8ByteLengthOf } from "@wetalk/domain/help/protocol";
import { scrubSecretShape } from "@wetalk/domain/help/scrub";
import { HELP_FORM_MARK_ATTRIBUTE, HELP_HOST_TAG } from "../core/path.js";
/** Name, id, label or aria words that mark a secret control: never read, not even listed. */
export const SNAPSHOT_SECRET_WORD = /password|passcode|secret|token|api[\s_-]?key|\bkey\b|iban|card|cvc|cvv|otp|one[\s_-]?time|2fa|two[\s_-]?factor|recovery|authenticator/iu;
/** `autocomplete` tokens of a secret control. */
export const SNAPSHOT_SECRET_AUTOCOMPLETE = /one-time-code|current-password|new-password|cc-/iu;
/** Input types never read. */
export const SNAPSHOT_NEVER_INPUT_TYPE = Object.freeze(["password", "hidden", "file"]);
const SKIPPED_TAG = new Set(["SCRIPT", "STYLE", "TEMPLATE", "NOSCRIPT", "SVG", "IFRAME", "CANVAS", "VIDEO", "AUDIO", "IMG"]);
const HEADING = /^H([1-6])$/u;
/** Elements whose whole text is one item when they hold no heading, table or control. */
const TEXT_BLOCK = new Set(["P", "LI", "DT", "DD", "BLOCKQUOTE", "FIGCAPTION", "LEGEND", "CAPTION", "SMALL", "PRE"]);
/** The shell's main column, else `<main>`, else the body. */
export function snapshotRootOf(doc) {
    return doc.querySelector(".c-main") ?? doc.querySelector("main") ?? doc.body;
}
function oneLine(text) {
    return scrubSecretShape(text.replace(/\s+/gu, " ").trim()).text;
}
function isHidden(element) {
    if (element.closest("[hidden],[aria-hidden='true'],[inert],[data-help-never]") !== null)
        return true;
    const dialog = element.closest("dialog");
    if (dialog !== null && !dialog.hasAttribute("open"))
        return true;
    const view = element.ownerDocument.defaultView;
    if (view !== null && typeof view.getComputedStyle === "function") {
        const style = view.getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden")
            return true;
    }
    return false;
}
function textOfIds(doc, ids) {
    return ids
        .split(/\s+/u)
        .map((one) => doc.getElementById(one)?.textContent ?? "")
        .join(" ");
}
/** A control's accessible name, roughly as a screen reader finds it. */
function labelOf(element) {
    const doc = element.ownerDocument;
    const aria = element.getAttribute("aria-label");
    if (aria !== null && aria.trim() !== "")
        return aria;
    const by = element.getAttribute("aria-labelledby");
    if (by !== null) {
        const named = textOfIds(doc, by);
        if (named.trim() !== "")
            return named;
    }
    const id = element.getAttribute("id");
    if (id !== null && id !== "") {
        for (const label of doc.querySelectorAll("label"))
            if (label.htmlFor === id)
                return label.textContent ?? "";
    }
    const wrapping = element.closest("label");
    if (wrapping !== null)
        return wrapping.textContent ?? "";
    if (element.tagName === "BUTTON")
        return element.textContent ?? "";
    return element.getAttribute("placeholder") ?? element.getAttribute("title") ?? "";
}
/** True for a control that is never read at all. */
export function isSecretControl(element) {
    const type = (element.getAttribute("type") ?? "").toLowerCase();
    if (element.tagName === "INPUT" && SNAPSHOT_NEVER_INPUT_TYPE.includes(type))
        return true;
    if (SNAPSHOT_SECRET_AUTOCOMPLETE.test(element.getAttribute("autocomplete") ?? ""))
        return true;
    const words = [element.getAttribute("name"), element.getAttribute("id"), element.getAttribute("aria-label"), labelOf(element)].join(" ");
    return SNAPSHOT_SECRET_WORD.test(words);
}
function cell(text) {
    return cutSnapshotText(oneLine(text), HELP_SNAPSHOT_CELL_CHAR_LIMIT);
}
function controlTypeOf(element) {
    const tag = element.tagName;
    if (tag === "SELECT")
        return "select";
    if (tag === "TEXTAREA")
        return "textarea";
    if (tag === "BUTTON")
        return "button";
    if (element.getAttribute("role") === "switch")
        return "switch";
    if (tag === "INPUT") {
        const type = (element.getAttribute("type") ?? "text").toLowerCase();
        if (type === "checkbox" || type === "radio" || type === "number" || type === "time" || type === "date")
            return type;
        if (type === "submit" || type === "button" || type === "reset")
            return "button";
        return "text";
    }
    return "other";
}
const NATIVE_CONTROL = new Set(["INPUT", "SELECT", "TEXTAREA", "BUTTON"]);
export function pageSnapshotOf(root, option) {
    const item = [];
    let truncated = false;
    let controlCount = 0;
    const push = (one, cut) => {
        if (cut)
            truncated = true;
        if (item.length >= HELP_SNAPSHOT_ITEM_LIMIT) {
            truncated = true;
            return;
        }
        item.push(one);
    };
    function text(value) {
        const line = oneLine(value);
        if (line === "")
            return;
        const cut = cutSnapshotText(line, HELP_SNAPSHOT_TEXT_CHAR_LIMIT);
        push({ kind: "text", text: cut.text }, cut.cut);
    }
    function control(element, actionableCode) {
        if (option.control === false || isSecretControl(element))
            return;
        const formId = actionableCode === null ? null : option.formOfField(actionableCode);
        controlCount += 1;
        const ref = formId !== null && actionableCode !== null ? `${formId}.${actionableCode}` : `c${String(controlCount)}`;
        const label = cell(labelOf(element));
        const type = controlTypeOf(element);
        let value;
        let optionList;
        let cut = label.cut;
        if (element.tagName === "INPUT") {
            const input = element;
            if (type === "checkbox" || type === "radio")
                value = input.checked;
            else if (type !== "button") {
                const read = cell(input.value);
                value = read.text;
                cut ||= read.cut;
            }
        }
        else if (element.tagName === "TEXTAREA") {
            const read = cell(element.value);
            value = read.text;
            cut ||= read.cut;
        }
        else if (element.tagName === "SELECT") {
            const select = element;
            const chosen = select.selectedOptions[0];
            if (chosen !== undefined)
                value = cell(chosen.textContent ?? "").text;
            const all = [...select.options].map((one) => cell(one.textContent ?? "").text);
            if (all.length > HELP_SNAPSHOT_OPTION_LIMIT)
                cut = true;
            optionList = all.slice(0, HELP_SNAPSHOT_OPTION_LIMIT);
        }
        else if (type === "switch") {
            value = element.getAttribute("aria-checked") === "true";
        }
        const enabled = !(element.hasAttribute("disabled") || element.getAttribute("aria-disabled") === "true");
        push({
            kind: "control",
            ref,
            label: label.text,
            type,
            ...(value === undefined ? {} : { value }),
            ...(optionList === undefined ? {} : { option: optionList }),
            enabled,
            actionable: ref.includes("."),
        }, cut);
    }
    function table(element) {
        const headerCells = [...element.querySelectorAll("thead th, thead td")];
        const rows = [...element.querySelectorAll("tbody tr")].filter((row) => !isHidden(row));
        let cut = headerCells.length > HELP_SNAPSHOT_TABLE_COLUMN_LIMIT || rows.length > HELP_SNAPSHOT_TABLE_ROW_LIMIT;
        const header = headerCells.slice(0, HELP_SNAPSHOT_TABLE_COLUMN_LIMIT).map((one) => {
            const read = cell(one.textContent ?? "");
            cut ||= read.cut;
            return read.text;
        });
        const row = rows.slice(0, HELP_SNAPSHOT_TABLE_ROW_LIMIT).map((one) => [...one.children].slice(0, HELP_SNAPSHOT_TABLE_COLUMN_LIMIT).map((each) => {
            const read = cell(each.textContent ?? "");
            cut ||= read.cut;
            return read.text;
        }));
        push({ kind: "table", header, row, more: Math.max(0, rows.length - HELP_SNAPSHOT_TABLE_ROW_LIMIT) }, cut);
    }
    function holdsStructure(element) {
        return element.querySelector("h1,h2,h3,h4,h5,h6,table,input,select,textarea,button,[role='alert'],[role='status'],[role='switch']") !== null;
    }
    function visit(element) {
        if (SKIPPED_TAG.has(element.tagName.toUpperCase()) || element.tagName.toLowerCase() === HELP_HOST_TAG)
            return;
        if (element.tagName === "LABEL") {
            // A control carries its label: a label's own words are never an item; a control wrapped in it still is.
            for (const child of element.children)
                if (NATIVE_CONTROL.has(child.tagName) || holdsStructure(child))
                    visit(child);
            return;
        }
        if (isHidden(element))
            return;
        const heading = HEADING.exec(element.tagName);
        if (heading !== null) {
            const line = oneLine(element.textContent ?? "");
            if (line !== "") {
                const cut = cutSnapshotText(line, HELP_SNAPSHOT_TEXT_CHAR_LIMIT);
                push({ kind: "heading", level: Number(heading[1]), text: cut.text }, cut.cut);
            }
            return;
        }
        const role = element.getAttribute("role");
        if (role === "alert" || role === "status" || element.classList.contains("wt-field-error")) {
            const line = oneLine(element.textContent ?? "");
            if (line !== "") {
                const cut = cutSnapshotText(line, HELP_SNAPSHOT_TEXT_CHAR_LIMIT);
                push({ kind: "status", tone: role === "status" ? "info" : "error", text: cut.text }, cut.cut);
            }
            return;
        }
        if (element.tagName === "TABLE") {
            table(element);
            return;
        }
        const mark = element.getAttribute(HELP_FORM_MARK_ATTRIBUTE.field);
        if (NATIVE_CONTROL.has(element.tagName) || role === "switch") {
            control(element, mark);
            return;
        }
        if (mark !== null) {
            // A composite fill target (the opening-hours editor, the language chips): one control; PAGE holds its value.
            control(element, mark);
            return;
        }
        if (TEXT_BLOCK.has(element.tagName) && !holdsStructure(element)) {
            text(element.textContent ?? "");
            return;
        }
        let own = "";
        for (const child of element.childNodes)
            if (child.nodeType === 3)
                own += child.textContent ?? "";
        text(own);
        for (const child of element.children)
            visit(child);
    }
    try {
        for (const child of root.children)
            visit(child);
    }
    catch {
        truncated = true;
    }
    // The whole snapshot inside the protocol's byte limit (with room for the frame's own keys): drop from the end.
    const fits = () => utf8ByteLengthOf(JSON.stringify({ type: "page_snapshot", snapshot: { item, truncated: true } })) <= HELP_PAGE_SNAPSHOT_BYTE_LIMIT;
    while (item.length > 0 && !fits()) {
        item.pop();
        truncated = true;
    }
    return { item, truncated };
}
//# sourceMappingURL=page-snapshot.js.map